
import {FC, useState} from "react";
import {TextInput} from "@interstate/components/TextInput";
import {useOperationalEntity} from "../../context";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntityParent as updateOperationalEntityParentMutation,
  updateOperationalEntityParentV2 as updateOperationalEntityParentV2Mutation
} from "../../../amplify/graphql";
import {useFlags} from "launchdarkly-react-client-sdk";
import {
  LookupOperationalEntity,
  UpdateOperationalEntityParentData,
  UpdateOperationalEntityParent,
  UpdateOperationalEntityParentVariables,
  UpdateOperationalEntityParentV2,
  UpdateOperationalEntityParentV2Data,
  UpdateOperationalEntityParentV2Variables,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  assertOperationalEntityPresent
} from "../../backend";
import {features} from "../../../features";
import {UpdateParentInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Endpoints} from "../../../runtime";
import {useTranslation} from "react-i18next";
import {empty, ErrorHandler, OnCompleteCallback} from "../../../utils";
import {ModalWithAlert, useAlert} from "../../../alerting";
import "./EditParentModal.scss";

export const EditParentModal: FC<{
  id: string;
  currentParent?: string;
  onComplete: OnCompleteCallback;
}> = ({id, currentParent, onComplete}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [inputErrorMessage, setInputErrorMessage] = useState<string>();
  const [working, setWorking] = useState(false);
  const [value, setValue] = useState(currentParent);
  const flags = useFlags();
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [updateParent] = useGraphqlMutation<
    UpdateOperationalEntityParentData,
    UpdateOperationalEntityParentVariables
  >({
    document: UpdateOperationalEntityParent,
    amplifyMutation: updateOperationalEntityParentMutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const [updateParentV2] = useGraphqlMutation<
    UpdateOperationalEntityParentV2Data,
    UpdateOperationalEntityParentV2Variables
  >({
    document: UpdateOperationalEntityParentV2,
    amplifyMutation: updateOperationalEntityParentV2Mutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const clearErrors = (): void => {
    clearAlert();
    setInputErrorMessage(undefined);
  };

  const handleError: ErrorHandler = (error: any) => {
    // TODO: We need a generic mechanism to indicate validation-related errors along with a reason-code
    error.message.includes("parent id:")
      ? setInputErrorMessage(error.message)
      : createErrorAlert(error);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveWithV1 = async (parentInput: UpdateParentInput) => {
    await updateParent({variables: {parentInput}})
      .then(result => {
        if (result.data?.entity.success === false) {
          throw new Error(t("common-errors.backend-error"));
        }
      })
      .then(refreshEntity);
  };

  const saveWithV2 = async (parentInput: UpdateParentInput) => {
    await updateParentV2({variables: {parentInput}}).then(result => {
      const entity = result.data?.result?.entity;
      entity ? setEntity(entity) : refreshEntity();
    });
  };

  const saveChanges = async () => {
    setWorking(true);
    clearErrors();
    const parentInput: UpdateParentInput = {
      parentId: value,
      entityId: id
    };
    const save = flags[features.dev.createOrg.createWithTenant]
      ? saveWithV2
      : saveWithV1;
    await save(parentInput)
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  const onInputChange = (event: any) => {
    setValue(event.target?.value.trim());
  };

  return (
    <ModalWithAlert
      id={"edit-parent-modal"}
      data-testid={"edit-parent-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-parent")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <TextInput
        id={"parent-text-input"}
        data-testid={"parent-text-input"}
        name={"parent"}
        label={`${t("modal.text-input.edit-parent")}`}
        value={value}
        onChange={onInputChange}
        onKeyDown={async (event: any) => {
          if (event.key === "Enter") {
            await saveChanges();
          }
        }}
        clearButton={!empty(value)}
        hasError={!!inputErrorMessage}
        errorMessage={inputErrorMessage}
      />
    </ModalWithAlert>
  );
};
